/**
 * `value_map` renderer — invisible auto-advancing step that maps combinations
 * of previous answers to computed values written to the answers bag.
 *
 * This is conceptually similar to `handoff_config` but generalised: instead
 * of resolving a single URL, it resolves an arbitrary set of key-value pairs
 * that downstream steps can reference via `template_fields` transforms.
 *
 * Step JSON shape:
 *
 *   {
 *     "type": "value_map",
 *     "advances_progress": false,
 *     "showProgress": false,
 *     "data": {
 *       "maps": [
 *         {
 *           "inputs": ["style", "audience"],
 *           "separator": "|",
 *           "lookup": {
 *             "anime|female": {
 *               "character_name": "Janice Cooper",
 *               "character_image": "https://cdn.nd-api.com/avatars/…",
 *               "character_url": "https://spicychat.ai/chatbot/…",
 *               "character_greeting": "*She looks up…*"
 *             }
 *           },
 *           "default": {
 *             "character_name": "May",
 *             "character_image": "https://…"
 *           }
 *         }
 *       ]
 *     }
 *   }
 *
 * For each map entry:
 *   1. Reads the answer values for each key in `inputs` from the answers bag.
 *   2. Joins them with `separator` (default `"|"`).
 *   3. Looks up the compound key in `lookup`.
 *   4. Falls back to `default` when the key is not found.
 *   5. Writes each key-value pair from the result into answers via `onAnswer`.
 *   6. Auto-advances to the next step (renders nothing).
 */
import { useEffect, useRef } from "react";
import { z } from "zod";

import { createRenderer } from "@survey-kit/registry";
import type { RendererProps } from "@survey-kit/types";

const mapEntrySchema = z.object({
  inputs: z.array(z.string().min(1)).min(1),
  separator: z.string().optional(),
  lookup: z.record(z.string(), z.record(z.string(), z.unknown())),
  default: z.record(z.string(), z.unknown()).optional(),
});

export const valueMapSchema = z
  .object({
    maps: z.array(mapEntrySchema).min(1),
  })
  .passthrough();

export type ValueMapData = z.infer<typeof valueMapSchema>;

const resolveMap = (
  entry: z.infer<typeof mapEntrySchema>,
  answers: Record<string, unknown>,
): Record<string, unknown> | null => {
  const separator = entry.separator ?? "|";
  const parts: string[] = [];

  for (const key of entry.inputs) {
    const val = answers[key];
    if (typeof val !== "string" || val.length === 0) {
      return entry.default ?? null;
    }
    parts.push(val);
  }

  const compoundKey = parts.join(separator);
  return entry.lookup[compoundKey] ?? entry.default ?? null;
};

const ValueMapRenderer = ({ data, answers, onAnswer, onAdvance }: RendererProps<ValueMapData>) => {
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (triggeredRef.current) return;
    triggeredRef.current = true;

    for (const mapEntry of data.maps) {
      const resolved = resolveMap(mapEntry, answers);
      if (resolved !== null) {
        for (const [key, value] of Object.entries(resolved)) {
          onAnswer(key, { value });
        }
      }
    }

    onAdvance();
  }, [data, answers, onAnswer, onAdvance]);

  return null;
};

export const valueMapRenderer = createRenderer({
  type: "value_map",
  dataSchema: valueMapSchema,
  label: "Value map",
  invisible: true,
  Component: ValueMapRenderer,
});

export default valueMapRenderer;
